import api from './api';
import {
  getBaselineErrors,
  getBaselineReport,
  getEvaluationRunErrors,
  getGuidelineDraft,
  getTuningSession,
  listEvaluationRuns,
  listTuningSessions,
} from './accuracyBackendApi';
import { normalizeDraftContent, draftHasCategories } from './guidelineDraftMetadata';
import { guidelineResponseToDraftContent } from './guidelineToDraftContent';
import type {
  AccuracyErrorItem,
  GuidelineDraftContent,
  SavedAccuracyReport,
  TuningSession,
  EvaluationRun,
} from '../types/accuracy';

export type DraftCompareTargetId = 'baseline' | 'latest_session' | 'previous_run';

export interface DraftCompareTarget {
  id: DraftCompareTargetId;
  label: string;
  content: GuidelineDraftContent;
}

export interface TuningDraftSeedResult {
  report: SavedAccuracyReport;
  initialDraft: GuidelineDraftContent;
  compareTargets: DraftCompareTarget[];
  latestSession: TuningSession | null;
}

async function loadProductionDraft(report: SavedAccuracyReport): Promise<GuidelineDraftContent | null> {
  const guidelineId = report.filters.guideline_id;
  const version = report.filters.guideline_version;
  if (!guidelineId || version == null) return null;
  try {
    const g = await api.guidelines.getGuideline(guidelineId, version);
    return guidelineResponseToDraftContent({
      guideline_id: guidelineId,
      base_version: version,
      guideline: g as any,
    });
  } catch {
    return null;
  }
}

export async function loadTuningDraftSeed(baselineReportId: string): Promise<TuningDraftSeedResult> {
  const report = await getBaselineReport(baselineReportId);
  const productionDraft = await loadProductionDraft(report);

  const sessions = (await listTuningSessions())
    .filter((s) => s.baseline_report_id === baselineReportId)
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const latestSession = sessions[0] ?? null;

  let latestSessionDraft: GuidelineDraftContent | null = null;
  if (latestSession) {
    try {
      const draft = await getGuidelineDraft(latestSession.draft_id);
      if (draftHasCategories(draft.content_json)) {
        latestSessionDraft = normalizeDraftContent(draft.content_json);
      }
    } catch {
      // ignore
    }
    if (!latestSessionDraft) {
      try {
        const runs = await listEvaluationRuns(latestSession.id);
        const latestRun = runs.sort((a, b) => b.run_number - a.run_number)[0];
        if (draftHasCategories(latestRun?.draft_snapshot)) {
          latestSessionDraft = normalizeDraftContent(latestRun!.draft_snapshot!);
        }
      } catch {
        // ignore
      }
    }
  }

  const fallback: GuidelineDraftContent = productionDraft ?? {
    guideline_id: report.filters.guideline_id ?? '',
    base_version: report.filters.guideline_version ?? 0,
    violation_categories: [],
  };

  const initialDraft = normalizeDraftContent(
    latestSessionDraft
      ? JSON.parse(JSON.stringify(latestSessionDraft))
      : JSON.parse(JSON.stringify(fallback))
  );

  const compareTargets: DraftCompareTarget[] = [];
  if (productionDraft) {
    compareTargets.push({
      id: 'baseline',
      label: `本番ガイドライン（v${productionDraft.base_version}）`,
      content: normalizeDraftContent(productionDraft),
    });
  }
  if (latestSessionDraft && latestSession) {
    compareTargets.push({
      id: 'latest_session',
      label: `最新チューニング（${latestSession.name}）`,
      content: normalizeDraftContent(latestSessionDraft),
    });
  }

  return {
    report,
    initialDraft,
    compareTargets,
    latestSession,
  };
}

export interface SessionEditorSeedResult {
  session: TuningSession;
  report: SavedAccuracyReport;
  draft: GuidelineDraftContent;
  draftId: string;
  compareTargets: DraftCompareTarget[];
  runs: EvaluationRun[];
  chatErrors: AccuracyErrorItem[];
  errorSourceLabel: string;
}

/** Load draft editor context for an existing tuning session page. */
export async function loadSessionEditorSeed(sessionId: string): Promise<SessionEditorSeedResult> {
  const session = await getTuningSession(sessionId);
  const [report, draftRecord, runs, seed] = await Promise.all([
    getBaselineReport(session.baseline_report_id),
    getGuidelineDraft(session.draft_id),
    listEvaluationRuns(sessionId),
    loadTuningDraftSeed(session.baseline_report_id),
  ]);

  const draft = normalizeDraftContent(draftRecord.content_json);

  let chatErrors = await getBaselineErrors(session.baseline_report_id);
  let errorSourceLabel = 'ベースライン評価';
  const latestRun = [...runs].sort((a, b) => b.run_number - a.run_number)[0];
  if (latestRun) {
    try {
      const runErrors = await getEvaluationRunErrors(latestRun.id);
      if (runErrors.length > 0) {
        chatErrors = runErrors;
        errorSourceLabel = `チューニング #${latestRun.run_number}`;
      }
    } catch {
      // keep baseline errors
    }
  }

  return {
    session,
    report,
    draft,
    draftId: draftRecord.id,
    compareTargets: seed.compareTargets,
    runs,
    chatErrors,
    errorSourceLabel,
  };
}
